import { useContext } from 'react';
import type { FC, ReactElement, Dispatch, SetStateAction } from 'react';
import classnames from 'classnames';
import { Link } from 'react-router-dom';
import type { LocaldataProps } from 'react-localdata';
import { LocaldataContext } from 'react-localdata';

import Icon from 'app/components/Icon';
import arrowIcon from 'app/icons/arrow.svg';

import AppLogo from './AppLogo';
import SidebarMenu from './SidebarMenu';
import BackButton from './BackButton';
import style from './style.scss';

interface ChildrenProps {
  collapsed: boolean;
}

interface Props {
  children?: ReactElement | ((props: ChildrenProps) => ReactElement | null);
  collapsable?: boolean;
}

const SidebarButton: FC<{
  collapsed: boolean;
  setCollapsed: Dispatch<SetStateAction<boolean>>;
}> = ({ collapsed, setCollapsed }) => {
  const buttonClassName = classnames(style.collapseButton, {
    [style.collapseButton_collapsed]: collapsed
  });

  const toogle = () => setCollapsed(value => !value);

  return (
    <button className={buttonClassName} onClick={toogle}>
      <Icon href={arrowIcon} className={style.collapseIcon} />
    </button>
  );
};

const Sidebar: FC<Props> = ({ children, collapsable = false }) => {
  const { localdata, saveLocaldata }: LocaldataProps<AppLocaldata> = useContext(LocaldataContext);
  const collapsed = collapsable ? localdata.menuCollapsed : false;
  const setCollapsed = () => {
    saveLocaldata({ menuCollapsed: !collapsed });
  };

  const navClassName = classnames(style.sidebar, {
    [style.sidebar_collapsed]: collapsed
  });

  return (
    <nav className={navClassName}>
      <div className={style.logoContainer}>
        <Link to="/">
          <AppLogo collapsed={collapsed} />
        </Link>
      </div>
      {collapsable && <SidebarButton collapsed={collapsed} setCollapsed={setCollapsed} />}
      <div className={style.content}>
        {typeof children === 'function' ? children({ collapsed }) : children}
      </div>
      <SidebarMenu collapsed={collapsed} />
    </nav>
  );
};

Sidebar.displayName = 'Sidebar';

export default Sidebar;
export { BackButton };
